/**
 * AI Desk — the nightly board.
 *
 * The page is built around one honesty rule: a reader must always be able to tell the difference
 * between "the AI looked and was unimpressed", "the AI never ran", and "the AI answered but broke
 * its contract". Those are three different facts and the UI never collapses them into a blank
 * space. The same rule governs the shortlist, which shows what did NOT surface and why — a board
 * that only ever displays its winners looks prescient in hindsight and teaches the reader nothing.
 *
 * In shadow mode the actions are limited to Watch and Dismiss. Both are recorded, because without
 * them there is no way to tell later whether the board was ignored for being wrong or for never
 * being read.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Chip, Button, Divider, Tooltip, CircularProgress, Collapse,
  Table, TableHead, TableRow, TableCell, TableBody, LinearProgress, Alert as MuiAlert,
  Tabs, Tab,
} from '@mui/material';
import PsychologyIcon from '@mui/icons-material/Psychology';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ScienceIcon from '@mui/icons-material/Science';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { useNavigate } from 'react-router-dom';
import { useAppTheme } from '../hooks/useAppTheme';
import aiService, { type AiBoard, type AiCandidate, type AiScorecard, type AiBucket, type AiQuantHealth, type AiBakeoffComparison } from '../services/ai.service';
import toast from 'react-hot-toast';
import OptionDeskPanel from '../components/ai/OptionDeskPanel';
import AiPerformancePanel from '../components/ai/AiPerformancePanel';
import AiIdeaCard from '../components/ai/AiIdeaCard';
import AiSpendStrip from '../components/ai/AiSpendStrip';
import AiEnginePanel from '../components/ai/AiEnginePanel';
import { useAiCost, costLabel, inr } from '../hooks/useAiCost';
import HelpLink from '../components/common/HelpLink';

/** Which part of the guide documents each tab. Keyed by tab index, like the Tabs value. */
const GUIDE_ANCHOR: Record<number, { anchor: string; label: string }> = {
  0: { anchor: 'signals', label: 'Signals' },
  1: { anchor: 'options', label: 'Option Desk' },
  2: { anchor: 'performance', label: 'Performance' },
  3: { anchor: 'engine', label: 'Engine' },
};

const AiDeskPage: React.FC = () => {
  const { C } = useAppTheme();
  const navigate = useNavigate();
  const { report: cost, disabled: aiOff, capReached, unavailableReason } = useAiCost();
  const [data, setData] = useState<AiBoard | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [showShortlist, setShowShortlist] = useState(false);
  const [showClusters, setShowClusters] = useState(false);
  const [card, setCard] = useState<AiScorecard | null>(null);
  const [horizon, setHorizon] = useState<1 | 5 | 10>(5);
  const [quant, setQuant] = useState<AiQuantHealth[]>([]);
  const [bake, setBake] = useState<AiBakeoffComparison | null>(null);
  /**
   * Which tab, from the URL.
   *
   * Read in the useState INITIALISER, not in an effect. Effects run after the first paint, so an
   * effect-set tab would render Signals for a frame and then swap -- and a read-only probe that
   * screenshots on load would capture the wrong tab entirely. It also makes the Performance view
   * linkable, which is the point of putting it in the URL at all.
   */
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    return t === 'options' ? 1 : t === 'performance' ? 2 : t === 'engine' ? 3 : 0;
  });

  // Keep the URL in step so a reload, a bookmark or a shared link lands where the user is.
  const selectTab = useCallback((v: number) => {
    setTab(v);
    const name = v === 1 ? 'options' : v === 2 ? 'performance' : v === 3 ? 'engine' : 'signals';
    const url = new URL(window.location.href);
    url.searchParams.set('tab', name);
    window.history.replaceState(null, '', url.toString());
  }, []);

  const load = useCallback(async () => {
    try {
      const b = await aiService.getBoard();
      setData(b);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // The scorecard is fetched separately: it resolves outcomes server-side and is slower than the
  // board, so a shared request would delay the thing the user came to read.
  useEffect(() => {
    let cancelled = false;
    aiService.getScorecard(horizon)
      .then(c => { if (!cancelled) setCard(c); })
      .catch(() => { if (!cancelled) setCard(null); });
    aiService.getBakeoff(horizon)
      .then(b => { if (!cancelled) setBake(b); })
      .catch(() => { if (!cancelled) setBake(null); });
    return () => { cancelled = true; };
  }, [horizon]);

  useEffect(() => {
    let cancelled = false;
    aiService.getQuantHealth()
      .then(q => { if (!cancelled) setQuant(q); })
      .catch(() => { if (!cancelled) setQuant([]); });
    return () => { cancelled = true; };
  }, []);

  // Poll only while a run is in flight. A board that changes once a night does not need a timer.
  const inFlight = data?.run?.status === 'PENDING' || data?.run?.status === 'SUBMITTED' || running;
  useEffect(() => {
    if (!inFlight) return;
    const t = setInterval(load, 15_000);
    return () => clearInterval(t);
  }, [inFlight, load]);

  const start = async (dryRun: boolean) => {
    setRunning(true);
    try {
      await aiService.runNightly(dryRun ? { dryRun: true } : {});
      toast.success(dryRun
        ? 'Dry run started — full pipeline, no model calls, no spend.'
        : 'Run started. The board updates when it completes.');
      setTimeout(load, 4000);
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || 'Could not start the run');
    } finally {
      setTimeout(() => setRunning(false), 4000);
    }
  };

  const act = async (c: AiCandidate, action: 'WATCHED' | 'DISMISSED') => {
    try {
      const updated = await aiService.setCandidateAction(c.id, c.humanAction === action ? 'NONE' : action);
      setData(d => d && ({
        ...d,
        board: d.board.map(x => x.id === updated.id ? { ...x, humanAction: updated.humanAction } : x),
      }));
    } catch {
      toast.error('Could not record that');
    }
  };

  const run = data?.run || null;
  const shadow = run?.mode === 'SHADOW';

  const gradeColor = (g: string | null) =>
    !g ? C.dim
      : g.startsWith('A') ? C.bull
        : g.startsWith('B') ? C.neutral
          : g === 'C' ? C.warn
            : C.bear;

  const clusterPeersOf = (c: AiCandidate): string[] => {
    if (!data?.clusters?.clusters || c.clusterId == null) return [];
    return (data.clusters.clusters[String(c.clusterId)] || []).filter(s => s !== c.symbol);
  };

  /**
   * RockBand's two reads for one candidate.
   *
   * Matched on the base timeframe, NOT on the label. The label is derived from the interval
   * actually fetched, so a string match would silently drop the chip the day that resolution
   * changes -- which is exactly what happened on 4 September.
   */
  const renderRbChips = (c: AiCandidate) => {
    const rbRegime = c.rockband?.find(r => r.ctfMinutes === 1440);
    const rbTiming = c.rockband?.find(r => r.ctfMinutes === 240);
    return (
      <>
        <RbChip label={rbRegime?.pair || '1D/1W'} r={rbRegime} C={C} />
        <RbChip label={rbTiming?.pair || '4H/1D'} r={rbTiming} C={C} />
      </>
    );
  };

  const cacheHitPct = useMemo(() => {
    if (!run) return null;
    const total = run.cacheReadTokens + run.tokensIn;
    return total > 0 ? Math.round((run.cacheReadTokens / total) * 100) : null;
  }, [run]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, maxWidth: 1400, mx: 'auto' }}>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
        <PsychologyIcon sx={{ color: C.accent }} />
        <Typography variant="h5" sx={{ fontWeight: 800 }}>AI Desk</Typography>
        {shadow && (
          <Chip size="small" label="SHADOW MODE" sx={{
            fontWeight: 800, bgcolor: C.warnMuted, color: C.warn, border: `1px solid ${C.warnBorder}`,
          }} />
        )}
        <Box sx={{ flex: 1 }} />
        {/* Per-tab, not one link at the top: each tab documents different machinery, and a reader
            who lands on Performance should not have to hunt through the Signals section first. */}
        <Tooltip arrow title={`Open the App Guide at the ${GUIDE_ANCHOR[tab]?.label || 'AI Desk'} section`}>
          <Button size="small" variant="text" startIcon={<MenuBookIcon />}
            onClick={() => window.open(
              `/app-guide#ai-desk/${GUIDE_ANCHOR[tab]?.anchor || 'what'}`, '_blank', 'noopener,noreferrer')}>
            Guide
          </Button>
        </Tooltip>
        {tab === 0 && (
          <>
            {/* Free must be as visible as billed: the dry run exercises the whole deterministic
                pipeline and makes no model call, and saying so stops it reading as "cheaper". */}
            <Tooltip arrow title="Runs the entire pipeline — universe, data-quality gate, RockBand scan and clustering — with no model calls at all. Costs nothing, and does not use up the night's scheduled run.">
              <span>
                <Button size="small" variant="outlined" startIcon={<ScienceIcon />}
                  disabled={running} onClick={() => start(true)}>
                  Dry run · ₹0
                </Button>
              </span>
            </Tooltip>
            <Tooltip arrow title={
              aiOff || capReached ? unavailableReason : (
                <span>
                  Runs the full pipeline and sends the shortlist to the model as a batch.
                  {cost?.perAction.nightlyRun != null && (
                    <>
                      <br /><br />
                      <b>Typical cost {inr(cost.perAction.nightlyRun)}</b> per run, measured from
                      the last {cost.perStage.find(s => s.stage === 'WIDE')?.n ?? 0} real runs
                      {cost.model.selected && <> on {cost.model.selected}</>}.
                      <br />Spent today {inr(cost.guard.spentTodayUsd)} of {inr(cost.guard.capUsd)}.
                    </>
                  )}
                </span>
              )
            }>
              <span>
                <Button size="small" variant="contained" startIcon={<PlayArrowIcon />}
                  disabled={running || aiOff || capReached} onClick={() => start(false)}>
                  {aiOff ? 'AI is off' : capReached ? 'Cap reached'
                    : `Run now${costLabel(cost?.perAction.nightlyRun) ? ` · ${costLabel(cost?.perAction.nightlyRun)!.replace('≈ ', '')}` : ''}`}
                </Button>
              </span>
            </Tooltip>
          </>
        )}
      </Box>

      <Tabs value={tab} onChange={(_, v) => selectTab(v)} sx={{ mb: 2, minHeight: 36 }}>
        <Tab label="Signals" sx={{ minHeight: 36, fontWeight: 700 }} />
        <Tab label="Options" sx={{ minHeight: 36, fontWeight: 700 }} />
        <Tab label="Performance" sx={{ minHeight: 36, fontWeight: 700 }} />
        {/* The statistical ranker that used to be its own page. One AI page, and the machinery
            sits behind the three tabs that answer a question rather than beside them. */}
        <Tab label="Engine" sx={{ minHeight: 36, fontWeight: 700 }} />
      </Tabs>

      {/* Spend belongs on the page that spends, not only on the Dashboard card. */}
      <AiSpendStrip />

      {tab === 3 ? <AiEnginePanel />
        : tab === 2 ? <AiPerformancePanel />
          : tab === 1 ? <OptionDeskPanel /> : (<>

      {shadow && (
        <MuiAlert severity="warning" variant="outlined" sx={{ mb: 2, py: 0.4 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Under evaluation. Everything here is recorded and scored but nothing is a recommendation.
            Watch and Dismiss are the only actions — they exist so the board can be judged later on
            what you actually thought of it, not just on what the market did.
          </Typography>
        </MuiAlert>
      )}

      {inFlight && <LinearProgress sx={{ mb: 2 }} />}

      {/* ── Run header ─────────────────────────────────────────────────── */}
      {run ? (
        <Box sx={{
          display: 'flex', flexWrap: 'wrap', gap: 2.5, alignItems: 'center',
          p: 1.5, mb: 2, borderRadius: 1, border: `1px solid ${C.neutralBorder}`, bgcolor: C.neutralMuted,
        }}>
          <Metric label="Status" value={run.status} color={
            run.status === 'COMPLETED' ? C.bull
              : run.status === 'PARTIAL' ? C.warn
                : ['FAILED', 'GATE_FAILED'].includes(run.status) ? C.bear : C.dim} />
          <Metric label="Universe" value={String(run.universeSize)} />
          <Metric label="With a signal" value={String(run.candidateCount)}
            hint="Symbols where RockBand had an entry condition on at least one timeframe pair." />
          <Metric label="Clusters" value={String(run.clusterCount)}
            hint={data?.clusters
              ? `Distinct bets after merging symbols correlated at ${data.clusters.threshold} or above over ${data.clusters.windowDays} days.`
              : undefined} />
          <Metric label="Sent to model" value={String(run.shortlistedCount)} />
          <Metric label="On the board" value={String(run.surfacedCount)} />
          <Metric label="Cost" value={inr(Number(run.costUsd) || 0)}
            hint={`What this run cost: ${(Number(run.costUsd) || 0).toFixed(3)} USD in the ledger, shown in rupees like every other billed control on the page.`} />
          {cacheHitPct != null && (
            <Metric label="Prompt cache" value={`${cacheHitPct}%`}
              color={cacheHitPct > 0 ? C.bull : C.warn}
              hint="Share of input tokens served from cache. Zero across a whole run means the stable prompt prefix is being invalidated, which quietly multiplies the bill." />
          )}
          {run.validationFailures > 0 && (
            <Metric label="Schema breaches" value={String(run.validationFailures)} color={C.warn}
              hint="How many replies in THIS run broke the output schema on the first attempt, counted before the corrective retry — the retry rescues the analysis but the breach still happened, and that rate is the health metric. Most of these are then rescued; the Scorecard's 'Unrescued' figure counts the ones that were not. Two different numbers, which is why they no longer share a name." />
          )}
          <Metric label="Model" value={run.model || '—'} small />
          <Metric label="Prompt" value={run.promptVersion || '—'} small />
          <Box sx={{ flex: 1 }} />
          <HelpLink to="ai-desk/run-strip" variant="icon" label="What each of these numbers means" />
        </Box>
      ) : (
        <MuiAlert severity="info" variant="outlined" sx={{ mb: 2 }}>
          No run yet. <b>Dry run</b> exercises the entire pipeline — universe, data-quality gate,
          RockBand scan and clustering — with no model calls and no spend. <b>Run now</b> does the
          same and then sends the shortlist to the model as a batch.
        </MuiAlert>
      )}

      {run?.abortReason && (
        <MuiAlert severity={run.status === 'GATE_FAILED' ? 'error' : 'warning'} sx={{ mb: 2 }}>
          <b>{run.status === 'GATE_FAILED' ? 'Run aborted — no spend incurred.' : 'Run was partial.'}</b>{' '}
          {run.abortReason}
        </MuiAlert>
      )}

      {/* Quant-ranker health. Small, but it surfaces the one thing that was previously
          invisible: an active model frozen because every newer one is being rejected. */}
      {!!quant.length && quant.some(q => q.state !== 'NONE') && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
          <Typography variant="caption" sx={{ color: C.dim, fontWeight: 800 }}>Quant ranker:</Typography>
          {quant.filter(q => q.state !== 'NONE').map(q => {
            const col = q.state === 'OK' ? C.bull : q.state === 'STALE' ? C.warn : C.bear;
            return (
              <Tooltip key={q.horizon} arrow title={
                q.state === 'NO_ACTIVE_MODEL'
                  ? 'No model is currently promoted for this horizon.'
                  : `v${q.activeVersion} trained ${q.activeAgeDays} days ago on ${q.trainSamples?.toLocaleString()} rows. ` +
                    `Lift up ${q.upLift ?? '—'}, down ${q.downLift ?? '—'} (lift, not raw precision — precision moves with the base rate ` +
                    `and is not comparable across periods or directions). ` +
                    `${q.rejectedSince ? `${q.rejectedSince} newer versions trained and not promoted. ` : ''}` +
                    `${q.latestBlockReason || ''}`
              }>
                <Chip size="small"
                  label={`${q.horizon.replace('NEXT_', '').toLowerCase()} v${q.activeVersion ?? '—'} · ${q.activeAgeDays ?? '—'}d${q.rejectedSince ? ` · ${q.rejectedSince} held back` : ''}`}
                  sx={{
                    height: 19, fontSize: '0.63rem', fontWeight: 700,
                    bgcolor: `${col}18`, color: col, border: `1px solid ${col}44`,
                  }} />
              </Tooltip>
            );
          })}
          <Typography variant="caption" sx={{ color: C.dim }}>
            not used to rank the board — RockBand generates candidates
          </Typography>
          {/* The full picture — corpus, lift per direction, why each challenger was refused, and
              the capture/label/retrain controls — is on the Engine tab, where the Edge AI page's
              contents landed. These two chips are the summary, not the whole story. */}
          <Button size="small" variant="text" sx={{ py: 0, minHeight: 0, textTransform: 'none', fontWeight: 700 }}
            onClick={() => selectTab(3)}>
            Engine detail
          </Button>
        </Box>
      )}

      {/* Scorecard. Above the ideas because during shadow it IS the point of the page. */}
      {card && (
        <Box sx={{ mb: 2.5, p: 1.5, borderRadius: 1, border: `1px solid ${C.accentBorder}`, bgcolor: C.accentMuted }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.8 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Scorecard</Typography>
            <Tooltip arrow title="Every figure is return minus Bitcoin's return over the identical window. In a universe of Bitcoin proxies an absolute number mostly measures the tape, so a grade that predicted it would have predicted nothing about the symbol.">
              <InfoOutlinedIcon sx={{ fontSize: 14, color: C.dim, cursor: 'help' }} />
            </Tooltip>
            <HelpLink to="ai-desk/signals" variant="icon" label="How to read the Scorecard" />
            <Box sx={{ flex: 1 }} />
            {([1, 5, 10] as const).map(h => (
              <Chip key={h} size="small" label={`${h}d`} onClick={() => setHorizon(h)}
                sx={{
                  height: 20, fontWeight: 700, cursor: 'pointer',
                  bgcolor: horizon === h ? C.accent : 'transparent',
                  // White on the bright dark-mode accent measured 2.72:1 — the worst contrast on
                  // the page. The accent is light in dark mode and dark in light mode, so the
                  // readable text colour flips with it.
                  color: horizon === h ? C.onSolid : C.dim,
                  border: `1px solid ${C.accentBorder}`,
                }} />
            ))}
          </Box>

          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>{card.verdict}</Typography>

          {/* The headline is computed from AI-kept vs baseline only. These two blocks say what it
              is leaving out: which buckets are still thin, and that the average is a blend of two
              results that disagree. Both are the difference between "this works" and "this works
              for the half of the book I happened to average". */}
          {!!card.graduation?.length && (
            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', gap: 0.6, alignItems: 'center', mb: 0.4 }}>
                <Typography variant="caption" sx={{ color: C.dim, fontWeight: 700 }}>
                  Evidence so far
                </Typography>
                <Tooltip arrow title={`Each bucket needs ${card.graduation[0]?.minN ?? 30} resolved outcomes of its own before its number is worth acting on. The verdict above is computed from AI-kept against the baseline, so a bucket short of the bar can sit underneath a confident sentence.`}>
                  <InfoOutlinedIcon sx={{ fontSize: 13, color: C.dim, cursor: 'help' }} />
                </Tooltip>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
                {card.graduation.map(g => (
                  <Tooltip key={g.label} arrow title={
                    g.ready
                      ? `${g.label}: ${g.n} resolved outcomes, at or above the ${g.minN} needed. Its number can be read.`
                      : `${g.label}: ${g.n} of ${g.minN} resolved outcomes. Too few — this bucket cannot yet be told apart from chance.`
                  }>
                    <Chip size="small"
                      label={`${g.label} ${g.n}/${g.minN}${g.ready ? ' ✓' : ''}`}
                      sx={{
                        height: 19, fontSize: '0.63rem', fontWeight: 700,
                        bgcolor: g.ready ? C.bullMuted : C.warnMuted,
                        color: g.ready ? C.bull : C.warn,
                      }} />
                  </Tooltip>
                ))}
              </Box>
            </Box>
          )}

          {card.directionCaveat && (
            <MuiAlert severity="warning" variant="outlined" sx={{ mb: 1, py: 0.2 }}>
              <Typography variant="caption" sx={{ lineHeight: 1.45 }}>{card.directionCaveat}</Typography>
            </MuiAlert>
          )}

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            <BucketChip b={card.baseline} C={C} />
            <BucketChip b={card.aiSelected} C={C} highlight />
            <BucketChip b={card.surfaced} C={C} />
            <BucketChip b={card.avoided} C={C} invert />
          </Box>

          {!!card.byGrade.length && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="caption" sx={{ color: C.dim, fontWeight: 700 }}>By grade:</Typography>
              {card.byGrade.map(g => (
                <Tooltip key={g.label} arrow title={`n=${g.n}, median ${g.medianRelPct ?? '—'}%, win rate ${g.winRate ?? '—'}%`}>
                  <Chip size="small"
                    label={`${g.label} ${g.meanRelPct == null ? '—' : `${g.meanRelPct > 0 ? '+' : ''}${g.meanRelPct}%`} (n=${g.n})`}
                    sx={{
                      height: 19, fontSize: '0.65rem', fontWeight: 700,
                      // A grade with no resolved outcomes is not a LOSS. `?? 0` painted every
                      // n=0 grade in the loss colour, which read as "grade C lost money" when
                      // the honest answer is "nothing is known yet" — and it was the lowest
                      // contrast text on the page as a side effect.
                      bgcolor: g.meanRelPct == null ? C.dimMuted
                        : g.meanRelPct > 0 ? C.bullMuted : C.bearMuted,
                      color: g.meanRelPct == null ? C.dim
                        : g.meanRelPct > 0 ? C.bull : C.bear,
                    }} />
                </Tooltip>
              ))}
            </Box>
          )}

          {bake && (
            <>
              <Divider sx={{ my: 1 }} />
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 800, display: 'block' }}>
                  Model bake-off vs {bake.challengerModel}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', color: C.dim }}>
                  {bake.verdict}
                </Typography>
                {bake.pairedN > 0 && (
                  <Typography variant="caption" sx={{ display: 'block', color: C.dim, mt: 0.3 }}>
                    They agreed on {bake.agreementPct ?? '—'}% of calls · challenger spend{' '}
                    {inr(bake.costChallengerUsd)} ·{' '}
                    <Tooltip arrow title={bake.costPrimaryBasis || 'Apportioned from the nightly batch cost.'}>
                      <span style={{ cursor: 'help', textDecoration: 'underline dotted' }}>
                        primary ≈ {inr(bake.costPrimaryUsd)}
                      </span>
                    </Tooltip>
                  </Typography>
                )}
              </Box>
            </>
          )}

          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
            {/* Three states, not two. "Measured and still growing" and "never measured" are
                different facts, and merging them is what let this line read 976 while the
                Performance tab read 345 + 108 for the same population. */}
            <Tooltip arrow title={
              `Counted over candidates that actually carried a RockBand signal — a scan with no ` +
              `entry condition is not evidence in flight. "Still maturing" has some closed bars ` +
              `but has not reached the ${card.horizonDays}-day horizon; "not measurable yet" has ` +
              `none, which is correct for about 36 hours after a run.`
            }>
              <Typography variant="caption" sx={{ color: C.dim, cursor: 'help' }}>
                <b>{card.resolvedCandidates}</b> resolved · <b>{card.maturingOutcomes}</b> still
                maturing · <b>{card.pendingOutcomes}</b> not measurable yet
                {card.skippedNoAnchor > 0 && <> · <b>{card.skippedNoAnchor}</b> unscoreable (no anchor price)</>}
              </Typography>
            </Tooltip>
            <Tooltip arrow title="How often the model admitted it was only restating RockBand. A high rate means the layer is not earning its cost, whatever the returns say.">
              <Typography variant="caption" sx={{ color: C.dim, cursor: 'help' }}>
                Restated RockBand: <b>{card.restatesRate.pct ?? '—'}%</b> of {card.restatesRate.n}
              </Typography>
            </Tooltip>
            <Tooltip arrow title="Share of assessed candidates across the whole corpus whose reply broke the output contract AND was not rescued by the corrective retry — rows persisted as VALIDATION_FAILED. It is NOT the run strip's 'Schema breaches', which counts first-attempt breaches within a single run before any retry. The two used to share the label 'Contract failures' and happened to read the same number, which made a coincidence look like a definition.">
              <Typography variant="caption" sx={{ color: C.dim, cursor: 'help' }}>
                Unrescued contract failures: <b>{card.contractFailureRate.pct ?? '—'}%</b>
              </Typography>
            </Tooltip>
          </Box>
        </Box>
      )}

      {/* ── Today's ideas ──────────────────────────────────────────────── */}
      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
        Today&apos;s ideas {!!data?.board?.length && <span style={{ color: C.dim }}>({data.board.length})</span>}
      </Typography>

      {!data?.board?.length ? (
        <MuiAlert severity="info" variant="outlined" sx={{ mb: 3 }}>
          {run
            ? 'Nothing cleared the bar in this run. That is a real answer, not a failure — most nights ' +
              'in a single correlated market genuinely offer one idea or none. The shortlist below shows ' +
              'what was considered and why each was set aside.'
            : 'Run the pipeline to produce a board.'}
        </MuiAlert>
      ) : (
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, mb: 3 }}>
          {data.board.map(c => (
            <AiIdeaCard
              key={c.id}
              c={c}
              peers={clusterPeersOf(c)}
              gradeColor={gradeColor}
              renderRbChips={renderRbChips}
              onAct={act}
            />
          ))}
        </Box>
      )}

      {/* ── Shortlist: what did not make it, and why ───────────────────── */}
      {!!data?.shortlist?.length && (
        <Box sx={{ mb: 2 }}>
          <Button size="small" onClick={() => setShowShortlist(v => !v)}
            startIcon={showShortlist ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            sx={{ fontWeight: 800 }}>
            Considered but not surfaced ({data.shortlist.length})
          </Button>
          <Collapse in={showShortlist}>
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800 }}>Symbol</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Stage</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Grade</TableCell>
                  <TableCell sx={{ fontWeight: 800 }} align="right">RockBand score</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Why not</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.shortlist.slice(0, 40).map(c => (
                  <TableRow key={c.id} hover>
                    <TableCell sx={{ fontWeight: 700 }}>{c.symbol}</TableCell>
                    <TableCell>
                      <Chip size="small" label={c.stage} sx={{
                        height: 18, fontSize: '0.6rem', fontWeight: 700,
                        bgcolor: c.stage === 'VALIDATION_FAILED' ? C.warnMuted : undefined,
                        color: c.stage === 'VALIDATION_FAILED' ? C.warn : undefined,
                      }} />
                    </TableCell>
                    <TableCell sx={{ color: gradeColor(c.grade), fontWeight: 700 }}>{c.grade || '—'}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {c.deterministicScore == null ? '—' : Number(c.deterministicScore).toFixed(0)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', color: C.dim }}>
                      {c.restatesSignal ? 'The model said it only restates RockBand — nothing added.' : (c.dropReason || '—')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Collapse>
        </Box>
      )}

      {/* ── Clusters ───────────────────────────────────────────────────── */}
      {data?.clusters && (
        <Box>
          <Button size="small" onClick={() => setShowClusters(v => !v)}
            startIcon={showClusters ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            sx={{ fontWeight: 800 }}>
            How the universe was grouped
          </Button>
          <Collapse in={showClusters}>
            <Typography variant="caption" sx={{ display: 'block', color: C.dim, mt: 0.5, mb: 1 }}>
              Symbols whose daily returns were correlated at {data.clusters.threshold} or above over{' '}
              {data.clusters.windowDays} days, measured pairwise — every member of a group is correlated
              with every other, not merely chained through a neighbour. One member per group is sent to
              the model, because the rest are the same bet.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
              {Object.entries(data.clusters.clusters)
                .filter(([, members]) => (members as string[]).length > 1)
                .sort((a, b) => (b[1] as string[]).length - (a[1] as string[]).length)
                .map(([id, members]) => (
                  <Chip key={id} size="small" label={(members as string[]).join(' · ')}
                    sx={{ height: 'auto', py: 0.4, fontSize: '0.68rem', fontWeight: 600 }} />
                ))}
              {!Object.values(data.clusters.clusters).some(m => (m as string[]).length > 1) && (
                <Typography variant="caption" sx={{ color: C.dim }}>
                  No group had more than one member — nothing in this run was redundant with anything else.
                </Typography>
              )}
            </Box>
          </Collapse>
        </Box>
      )}
      </>)}
    </Box>
  );
};

/**
 * One comparison bucket. The sample size is part of the LABEL, not tucked into a tooltip: a mean
 * over four observations should never be readable as a result at a glance.
 */
const BucketChip: React.FC<{ b: AiBucket; C: any; highlight?: boolean; invert?: boolean }> =
  ({ b, C, highlight, invert }) => {
    const v = b.meanRelPct;
    // For the AVOID bucket a NEGATIVE relative return means the model was RIGHT to pass, so the
    // colour is inverted rather than painting a correct call as a bad outcome.
    const good = v == null ? null : (invert ? v < 0 : v > 0);
    const col = good == null ? C.dim : good ? C.bull : C.bear;
    return (
      <Tooltip arrow title={
        `${b.label}. Mean ${v ?? '—'}% vs Bitcoin, median ${b.medianRelPct ?? '—'}%, ` +
        `win rate ${b.winRate ?? '—'}%, over ${b.n} resolved candidates.` +
        (invert ? ' For AVOID, a negative number means the model was right to pass.' : '')
      }>
        <Box sx={{
          px: 1, py: 0.5, borderRadius: 0.8,
          border: `${highlight ? 2 : 1}px solid ${highlight ? col : C.neutralBorder}`,
          bgcolor: `${col}12`, minWidth: 150,
        }}>
          <Typography variant="caption" sx={{ display: 'block', color: C.dim, fontWeight: 700 }}>
            {b.label}
          </Typography>
          <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: col, fontVariantNumeric: 'tabular-nums' }}>
            {v == null ? '—' : `${v > 0 ? '+' : ''}${v}%`}
            <Typography component="span" variant="caption" sx={{ color: C.dim, ml: 0.6, fontWeight: 600 }}>
              n={b.n}
            </Typography>
          </Typography>
        </Box>
      </Tooltip>
    );
  };

const Metric: React.FC<{ label: string; value: string; color?: string; hint?: string; small?: boolean }> =
  ({ label, value, color, hint, small }) => {
    const { C } = useAppTheme();
    const body = (
      <Box>
        <Typography variant="caption" sx={{ color: C.dim, display: 'flex', alignItems: 'center', gap: 0.3 }}>
          {label}
          {hint && <InfoOutlinedIcon sx={{ fontSize: 12, cursor: 'help' }} />}
        </Typography>
        <Typography sx={{
          fontWeight: 800, fontSize: small ? '0.72rem' : '0.95rem',
          color: color || 'inherit', fontVariantNumeric: 'tabular-nums',
        }}>
          {value}
        </Typography>
      </Box>
    );
    return hint ? <Tooltip arrow title={hint}>{body}</Tooltip> : body;
  };

/** RockBand's read on one timeframe pair, shown so agreement is visible rather than implied. */
const RbChip: React.FC<{ label: string; r: any; C: any }> = ({ label, r, C }) => {
  if (!r || r.status !== 'OK') {
    return (
      <Tooltip arrow title={`RockBand ${label}: ${r?.note || 'unavailable'}`}>
        <Chip size="small" label={`${label} —`} sx={{ height: 18, fontSize: '0.62rem', bgcolor: C.dimMuted, color: C.dim }} />
      </Tooltip>
    );
  }
  const dir = r.buyCond ? 'LONG' : r.shortCond ? 'SHORT' : 'flat';
  const col = r.buyCond ? C.bull : r.shortCond ? C.bear : C.dim;
  const ext = r.bandPosition == null ? ''
    : r.bandPosition > 1 ? ` · ${(r.bandPosition - 1).toFixed(2)} above band`
      : r.bandPosition < 0 ? ` · ${(-r.bandPosition).toFixed(2)} below band`
        : '';
  return (
    <Tooltip arrow title={
      `RockBand ${label}: ${dir}. Price sits at ${r.bandPosition ?? '—'} of the envelope ` +
      `(0 = lower band, 1 = upper band). Envelope width ${r.bandWidthPct ?? '—'}% of price.`
    }>
      <Chip size="small" label={`${label} ${dir}${ext}`}
        sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: `${col}18`, color: col }} />
    </Tooltip>
  );
};

export default AiDeskPage;
